/* Phase 55.1 — SEO Brand & Model landing pages.
 *
 * Two variants in one component (driven by useParams):
 *   /:locale/autos/:brand          — brand landing
 *   /:locale/autos/:brand/:model   — brand+model landing
 *
 * The `autos` segment is the DE slug. FR uses `voitures`, IT `auto`, EN `cars`.
 * App.js mounts four route entries (one per locale) all pointing here.
 *
 * SEO:
 *  - <title>, meta description, OG/Twitter tags injected into <head>
 *  - canonical + 4 hreflang alternates (one per locale)
 *  - JSON-LD: BreadcrumbList + ItemList (latest listings)
 *  - Long-form content via labels.* injected by backend (per-locale prose)
 */
import React, { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { L as Link } from "../lib/i18n";
import { api } from "../lib/api";
import { useLang } from "../contexts/LanguageContext";
import { Loader2, ArrowRight, ChevronRight, Car, BellPlus } from "lucide-react";
import CarCard from "../components/CarCard";
import { SEO_BRAND_PATH_BY_LOCALE, brandSeoPath } from "../lib/seoSlug";
import { buildSeoBrandBreadcrumb, buildSeoListingsItemList } from "../lib/schema";

const SITE_URL = (process.env.REACT_APP_PUBLIC_URL || "").replace(/\/$/, "");
const FILE_BASE = process.env.REACT_APP_BACKEND_URL;

function setMeta(selector, content) {
  if (!content) return;
  let el = document.head.querySelector(selector);
  if (!el) {
    el = document.createElement("meta");
    const m = selector.match(/\[(name|property)="([^"]+)"\]/);
    if (m) el.setAttribute(m[1], m[2]);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}
function setLink(rel, href, hreflang) {
  const sel = hreflang ? `link[rel="alternate"][hreflang="${hreflang}"]` : `link[rel="${rel}"]`;
  let el = document.head.querySelector(sel);
  if (!el) {
    el = document.createElement("link");
    el.rel = rel;
    if (hreflang) el.setAttribute("hreflang", hreflang);
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
  return el;
}

function useSeoHead(data, brandSlug, modelSlug) {
  useEffect(() => {
    if (!data?.ok) return;
    const title = data.hero.meta_title;
    const desc = data.hero.meta_description;
    // Phase 67.3 — Aliases canonicalise to backend-resolved slugs so e.g.
    // /de/autos/mercedes points its canonical at /de/autos/mercedes-benz.
    const canonBrand = data.canonical_brand_slug || brandSlug;
    const canonModel = data.canonical_model_slug || modelSlug;
    const canonOrigin = SITE_URL || window.location.origin;
    // Build the locale-aware canonical path from CURRENT locale + canonical slugs
    const locale = (data.locale || "de").toLowerCase();
    const seg = SEO_BRAND_PATH_BY_LOCALE[locale] || SEO_BRAND_PATH_BY_LOCALE.de;
    const canonTail = canonModel ? `${canonBrand}/${canonModel}` : canonBrand;
    const canonPath = `/${locale}/${seg}/${canonTail}`;
    document.title = title;
    setMeta('meta[name="description"]', desc);
    setMeta('meta[property="og:title"]', data.hero.h1);
    setMeta('meta[property="og:description"]', desc);
    setMeta('meta[property="og:type"]', "website");
    setMeta('meta[property="og:site_name"]', "CarHive");
    setMeta('meta[property="og:url"]', `${canonOrigin}${canonPath}`);
    // Phase 55.2 — dynamic OG image from first available listing photo.
    const firstImg = (data.latest || []).find((l) => l.images && l.images.length);
    const ogImg = firstImg
      ? `${FILE_BASE}/api/files/${firstImg.images[0]}`
      : `${canonOrigin}/icons/og-default.png`;
    setMeta('meta[property="og:image"]', ogImg);
    setMeta('meta[property="og:image:alt"]', data.hero.h1);
    setMeta('meta[name="twitter:card"]', "summary_large_image");
    setMeta('meta[name="twitter:title"]', data.hero.h1);
    setMeta('meta[name="twitter:description"]', desc);
    setMeta('meta[name="twitter:image"]', ogImg);
    // hreflang: ALWAYS use canonical slugs (never the alias) so all 5 locale
    // alternates point at the same logical URL across locales.
    Object.entries(SEO_BRAND_PATH_BY_LOCALE).forEach(([lc, lcSeg]) => {
      const tail = canonModel ? `${canonBrand}/${canonModel}` : canonBrand;
      const el = setLink("alternate", `${canonOrigin}/${lc}/${lcSeg}/${tail}`, lc);
      if (el) el.setAttribute("data-seo-brand", "1");
    });
    const xdefault = `${canonOrigin}/de/autos/${canonModel ? `${canonBrand}/${canonModel}` : canonBrand}`;
    const xd = setLink("alternate", xdefault, "x-default");
    if (xd) xd.setAttribute("data-seo-brand", "1");
    setLink("canonical", `${canonOrigin}${canonPath}`);
    return () => {
      document.head.querySelectorAll('link[rel="alternate"][data-seo-brand="1"]').forEach((n) => n.remove());
    };
  }, [data, brandSlug, modelSlug]);
}

function buildBreadcrumbJsonLd(locale, data, brandSlug, modelSlug) {
  // Phase 55.2 — delegate to shared schema builder for consistent structure.
  return buildSeoBrandBreadcrumb({
    siteUrl: SITE_URL || (typeof window !== "undefined" ? window.location.origin : ""),
    locale,
    brand: data.brand,
    brandSlug,
    model: data.kind === "model" ? data.model : undefined,
    modelSlug: data.kind === "model" ? modelSlug : undefined,
    allBrandsLabel: data.labels?.all_brands,
  });
}

function buildItemListJsonLd(data) {
  if (!data?.latest || data.latest.length === 0) return null;
  // Phase 55.2 — rich Car-typed ItemList children (eligible for Google's carousel rich result).
  return buildSeoListingsItemList({
    siteUrl: SITE_URL || (typeof window !== "undefined" ? window.location.origin : ""),
    locale: data.locale,
    listings: data.latest,
    fileBaseUrl: FILE_BASE,
    listName: data.hero.h1,
  });
}

function JsonLd({ data, id }) {
  useEffect(() => {
    if (!data) return;
    const el = document.createElement("script");
    el.type = "application/ld+json";
    el.dataset.seoLd = id;
    el.textContent = JSON.stringify(data);
    document.head.appendChild(el);
    return () => {
      document.querySelectorAll(`script[data-seo-ld="${id}"]`).forEach((n) => n.remove());
    };
  }, [data, id]);
  return null;
}

export default function BrandSeoPage() {
  const { brand: brandSlug, model: modelSlug } = useParams();
  const { lang } = useLang();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!brandSlug) return;
    setLoading(true);
    setError(null);
    const p = modelSlug
      ? api.seoBrandModel(brandSlug, modelSlug, lang)
      : api.seoBrand(brandSlug, lang);
    p.then((r) => {
      if (!r?.ok) { setError(r?.reason || "not_found"); setData(null); }
      else setData(r);
    }).catch(() => setError("network")).finally(() => setLoading(false));
  }, [brandSlug, modelSlug, lang]);

  useSeoHead(data, brandSlug, modelSlug);
  const crumbLd = useMemo(
    () => data?.ok ? buildBreadcrumbJsonLd(lang, data, brandSlug, modelSlug) : null,
    [data, lang, brandSlug, modelSlug],
  );
  const listLd = useMemo(() => buildItemListJsonLd(data), [data]);

  if (loading) {
    return (
      <div className="mx-auto flex max-w-6xl items-center gap-2 px-4 py-24 text-sm text-zinc-500 sm:px-6" data-testid="brand-seo-loading">
        <Loader2 className="h-4 w-4 animate-spin" /> {({ de: "Lade…", fr: "Chargement…", it: "Caricamento…", en: "Loading…" })[lang] || "Loading…"}
      </div>
    );
  }
  if (error || !data) {
    const notFound = { de: "Seite nicht gefunden", fr: "Page introuvable", it: "Pagina non trovata", en: "Page not found" }[lang] || "Page not found";
    const cta = { de: "Alle Fahrzeuge", fr: "Tous les véhicules", it: "Tutti i veicoli", en: "All vehicles" }[lang] || "All vehicles";
    return (
      <div className="mx-auto max-w-3xl px-4 py-24 text-center sm:px-6" data-testid="brand-seo-empty">
        <p className="text-xs font-semibold uppercase tracking-[0.25em] text-red-500">SEO</p>
        <h1 className="mt-2 font-display text-3xl font-bold text-white">{notFound}</h1>
        <Link to="/listings" className="mt-6 inline-block rounded-md bg-red-600 px-4 py-2 text-sm font-semibold text-white hover:bg-red-500" data-testid="brand-seo-empty-cta">
          {cta}
        </Link>
      </div>
    );
  }

  const isModel = data.kind === "model";
  const filterTo = isModel
    ? `/listings?brand=${encodeURIComponent(data.brand)}&model=${encodeURIComponent(data.model)}`
    : `/listings?brand=${encodeURIComponent(data.brand)}`;

  return (
    <article className="mx-auto max-w-7xl px-4 pb-20 pt-8 sm:px-6 lg:px-8" data-testid="brand-seo-page">
      <JsonLd id="breadcrumb" data={crumbLd} />
      <JsonLd id="itemlist" data={listLd} />

      {/* Breadcrumbs */}
      <nav className="flex flex-wrap items-center gap-1.5 text-xs text-zinc-500" aria-label="Breadcrumbs" data-testid="brand-seo-breadcrumbs">
        <Link to="/" className="hover:text-zinc-300">CarHive</Link>
        <ChevronRight className="h-3 w-3" />
        <span className="hover:text-zinc-300">{data.labels.all_brands || "Brands"}</span>
        <ChevronRight className="h-3 w-3" />
        {isModel ? (
          <>
            <Link to={brandSeoPath(lang, brandSlug)} className="hover:text-zinc-300" data-testid="brand-seo-crumb-brand">
              {data.brand}
            </Link>
            <ChevronRight className="h-3 w-3" />
            <span className="text-zinc-300">{data.model}</span>
          </>
        ) : (
          <span className="text-zinc-300">{data.brand}</span>
        )}
      </nav>

      {/* Hero */}
      <header className="mt-6 overflow-hidden rounded-2xl border border-zinc-800 bg-gradient-to-br from-red-950/30 via-zinc-950 to-black px-5 py-10 sm:px-10 sm:py-14" data-testid="brand-seo-hero">
        <p className="text-xs font-semibold uppercase tracking-[0.3em] text-red-500">
          CarHive · {({ de: "Schweiz", fr: "Suisse", it: "Svizzera", en: "Switzerland" })[lang] || "Schweiz"}
        </p>
        <h1 className="mt-3 max-w-4xl font-display text-3xl font-bold leading-tight text-white sm:text-5xl lg:text-6xl" data-testid="brand-seo-h1">
          {data.hero.h1}
        </h1>
        <p className="mt-3 max-w-3xl text-sm text-zinc-300 sm:text-base">{data.hero.subtitle}</p>
        <div className="mt-6 flex flex-wrap items-center gap-3">
          {data.count > 0 && (
            <Link to={filterTo} className="inline-flex items-center gap-1.5 rounded-md bg-red-600 px-4 py-2 text-sm font-semibold text-white hover:bg-red-500" data-testid="brand-seo-cta-filter">
              <Car className="h-4 w-4" /> {data.count} {data.labels.view_all || "Inserate"} <ArrowRight className="h-3 w-3" />
            </Link>
          )}
          <Link to={`/saved-searches?brand=${encodeURIComponent(data.brand)}${isModel ? `&model=${encodeURIComponent(data.model)}` : ""}`} className={`inline-flex items-center gap-1.5 rounded-md px-4 py-2 text-sm font-semibold text-white ${data.count > 0 ? "border border-zinc-700 bg-zinc-950 hover:border-red-500/40" : "bg-red-600 hover:bg-red-500"}`} data-testid="brand-seo-save-search">
            <BellPlus className="h-4 w-4" /> {data.labels.save_search}
          </Link>
        </div>
      </header>

      {/* Phase 67 — SEO intro copy (brand + model pages, curated or templated) */}
      {Array.isArray(data.hero.intro_paragraphs) && data.hero.intro_paragraphs.length > 0 && (
        <section className="mt-10" data-testid="brand-seo-intro">
          <div className="max-w-4xl space-y-4 text-sm leading-relaxed text-zinc-300 sm:text-base sm:leading-loose">
            {data.hero.intro_paragraphs.map((p, i) => (
              <p key={i} data-testid={`brand-seo-intro-p${i}`}>{p}</p>
            ))}
          </div>
        </section>
      )}

      {/* Popular models (brand page only) */}
      {!isModel && data.top_models && data.top_models.length > 0 && (
        <section className="mt-12" data-testid="brand-seo-top-models">
          <h2 className="font-display text-2xl font-bold text-white sm:text-3xl">{data.labels.popular_models}</h2>
          <div className="mt-5 grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-4">
            {data.top_models.map((m) => (
              <Link
                key={m.slug}
                to={brandSeoPath(lang, brandSlug, m.slug)}
                className="group flex items-center justify-between rounded-lg border border-zinc-800 bg-zinc-950 px-3.5 py-2.5 transition hover:border-red-500/40"
                data-testid={`brand-seo-model-pill-${m.slug}`}
              >
                <span className="truncate text-sm text-zinc-200 group-hover:text-red-300">{m.model}</span>
                <span className="ml-2 rounded-full bg-zinc-800 px-2 py-0.5 text-[10px] text-zinc-400 group-hover:bg-red-600/20 group-hover:text-red-300">
                  {m.count}
                </span>
              </Link>
            ))}
          </div>
        </section>
      )}

      {/* Latest listings */}
      <section className="mt-12" data-testid="brand-seo-latest">
        <h2 className="font-display text-2xl font-bold text-white sm:text-3xl">{data.labels.latest_h2}</h2>
        {data.latest.length === 0 ? (
          <div className="mt-5 rounded-2xl border border-zinc-800 bg-zinc-950/50 px-5 py-8 sm:px-8 sm:py-10" data-testid="brand-seo-empty-state">
            <p className="text-base text-zinc-200">{data.labels.no_results}</p>
            <div className="mt-5 flex flex-wrap items-center gap-3">
              <Link
                to={`/saved-searches?brand=${encodeURIComponent(data.brand)}${isModel ? `&model=${encodeURIComponent(data.model)}` : ""}`}
                className="inline-flex items-center gap-1.5 rounded-md bg-red-600 px-4 py-2 text-sm font-semibold text-white hover:bg-red-500"
                data-testid="brand-seo-empty-save-search"
              >
                <BellPlus className="h-4 w-4" /> {data.labels.save_search}
              </Link>
              <Link
                to="/listings"
                className="inline-flex items-center gap-1.5 rounded-md border border-zinc-700 bg-zinc-950 px-4 py-2 text-sm font-semibold text-white hover:border-red-500/40"
                data-testid="brand-seo-empty-browse-all"
              >
                <Car className="h-4 w-4" /> {data.labels.all_brands ? ({
                  de: "Alle Fahrzeuge ansehen",
                  fr: "Voir tous les véhicules",
                  it: "Vedi tutti i veicoli",
                  en: "Browse all vehicles",
                }[lang] || "Alle Fahrzeuge ansehen") : "Browse all"} <ArrowRight className="h-3 w-3" />
              </Link>
            </div>
          </div>
        ) : (
          <div className="mt-5 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {data.latest.map((car) => (
              <CarCard key={car.id} car={car} />
            ))}
          </div>
        )}
        {data.count > data.latest.length && (
          <div className="mt-6 text-center">
            <Link to={filterTo} className="inline-flex items-center gap-1.5 rounded-md border border-zinc-700 bg-zinc-950 px-5 py-2.5 text-sm font-semibold text-white hover:border-red-500/40" data-testid="brand-seo-view-all">
              {data.labels.view_all} ({data.count}) <ArrowRight className="h-3 w-3" />
            </Link>
          </div>
        )}
      </section>

      {/* Related models (model page only) */}
      {isModel && data.related_models && data.related_models.length > 0 && (
        <section className="mt-14" data-testid="brand-seo-related-models">
          <h2 className="font-display text-2xl font-bold text-white sm:text-3xl">{data.labels.related_models_h2}</h2>
          <div className="mt-5 flex flex-wrap gap-2">
            {data.related_models.map((m) => (
              <Link
                key={m.slug}
                to={brandSeoPath(lang, brandSlug, m.slug)}
                className="group inline-flex items-center gap-2 rounded-full border border-zinc-800 bg-zinc-950 px-4 py-2 text-sm text-zinc-200 hover:border-red-500/40 hover:text-red-300"
                data-testid={`brand-seo-related-${m.slug}`}
              >
                {data.brand} {m.model}
                <span className="rounded-full bg-zinc-800 px-2 py-0.5 text-[10px] text-zinc-400 group-hover:bg-red-600/20 group-hover:text-red-300">
                  {m.count}
                </span>
              </Link>
            ))}
          </div>
          <div className="mt-6">
            <Link to={brandSeoPath(lang, brandSlug)} className="inline-flex items-center gap-1.5 text-sm text-zinc-400 hover:text-red-300" data-testid="brand-seo-back-to-brand">
              ← {data.labels.back_to_brand}
            </Link>
          </div>
        </section>
      )}

      {/* Related brands (brand page only) */}
      {!isModel && data.related_brands && data.related_brands.length > 0 && (
        <section className="mt-14" data-testid="brand-seo-related-brands">
          <h2 className="font-display text-2xl font-bold text-white sm:text-3xl">{data.labels.related_brands_h2}</h2>
          <div className="mt-5 flex flex-wrap gap-2">
            {data.related_brands.map((b) => (
              <Link
                key={b.slug}
                to={brandSeoPath(lang, b.slug)}
                className="group inline-flex items-center gap-2 rounded-full border border-zinc-800 bg-zinc-950 px-4 py-2 text-sm text-zinc-200 hover:border-red-500/40 hover:text-red-300"
                data-testid={`brand-seo-related-brand-${b.slug}`}
              >
                {b.brand}
                <span className="rounded-full bg-zinc-800 px-2 py-0.5 text-[10px] text-zinc-400 group-hover:bg-red-600/20 group-hover:text-red-300">
                  {b.count}
                </span>
              </Link>
            ))}
          </div>
        </section>
      )}
    </article>
  );
}
